import { useEffect, useState } from 'react'

export interface WorkstationPageContext {
  surface?: string
  section?: string
  view?: string
  project?: Record<string, unknown>
  dataset?: Record<string, unknown>
  selection?: Record<string, unknown>
  filters?: unknown[]
  visualization?: Record<string, unknown>
  result?: Record<string, unknown>
}

const contexts = new Map<string, WorkstationPageContext>()
const listeners = new Set<() => void>()

function notify() {
  listeners.forEach((listener) => listener())
}

export function useWorkstationPageContext(source: string, context: WorkstationPageContext) {
  const serialized = JSON.stringify(context)

  useEffect(() => {
    contexts.set(source, context)
    notify()
    return () => {
      contexts.delete(source)
      notify()
    }
  // The serialized value is the stable change signal; callers can use plain
  // object literals without causing registration churn on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, serialized])
}

/** The registered page context, re-rendering when a page changes it (for what the chat shows as its context). */
export function useRegisteredPageContext(): Record<string, unknown> {
  const [, setVersion] = useState(0)
  useEffect(() => {
    const listener = () => setVersion((value) => value + 1)
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [])
  return Array.from(contexts.values()).reduce<Record<string, unknown>>(
    (result, context) => deepMerge(result, context as Record<string, unknown>),
    {},
  )
}

export function currentWorkstationPageContext(): Record<string, unknown> {
  const registered = Array.from(contexts.values()).reduce<Record<string, unknown>>(
    (result, context) => deepMerge(result, context as Record<string, unknown>),
    {},
  )

  return {
    route: `${window.location.pathname}${window.location.search}`,
    title: document.title,
    active_controls: activeControlLabels(),
    captured_at: new Date().toISOString(),
    ...registered,
  }
}

function activeControlLabels(): string[] {
  const selector = [
    '[role="tab"][aria-selected="true"]',
    '[role="tab"][data-state="active"]',
    '[aria-pressed="true"]',
  ].join(',')

  return Array.from(document.querySelectorAll<HTMLElement>(selector))
    .filter((element) => element.offsetParent !== null)
    .map((element) => element.innerText.trim())
    .filter(Boolean)
    .slice(0, 12)
}

function deepMerge(
  current: Record<string, unknown>,
  incoming: Record<string, unknown>,
): Record<string, unknown> {
  const result = { ...current }
  Object.entries(incoming).forEach(([key, value]) => {
    const previous = result[key]
    if (isPlainObject(previous) && isPlainObject(value)) {
      result[key] = deepMerge(previous, value)
    } else {
      result[key] = value
    }
  })
  return result
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}
